import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api, type EntityAlias, type EntityIdentifier, type EntityInput, type EntityKind, type Instrument, type LookupResult, type Proposal } from '../api';
import { Chip, EmptyState, ErrorState, Loading, Notice, PageIntro, useAsync } from '../components/ui';
import { relativeAge } from '../format';

export const KIND_LABELS: Record<EntityKind, string> = {
  company: 'Unternehmen',
  organisation: 'Organisation',
  authority: 'Behörde',
  person: 'Person (öffentliche Rolle)',
};

const REGISTRY_LABELS: Record<string, string> = { gleif: 'GLEIF', wikidata: 'Wikidata', zefix: 'Zefix' };

/** Registry status details from the API (internal/subjects/registry.go), in German. */
function detailText(d?: string): string {
  if (!d) return '';
  if (d.startsWith('no credentials configured')) return 'kein Zugang zur offiziellen Zefix-Schnittstelle eingerichtet';
  if (d.startsWith('this registry does not answer')) return 'beantwortet diese Art Anfrage nicht';
  if (d.endsWith(': unreachable')) return 'nicht erreichbar';
  if (d === 'registry lookup is switched off') return 'Nachschlagen ist abgeschaltet';
  return d;
}

const SCHEME_LABELS: Record<EntityIdentifier['scheme'], string> = { lei: 'LEI', che_uid: 'CHE-UID', wikidata: 'Wikidata', domain: 'Web-Domain' };

/**
 * Deine Subjekte (ADR-031): companies, organisations, authorities and people in
 * a public role, tracked across stories. A subject starts from a name or an
 * identifier, looked up in public registries (only what is typed is sent) and
 * confirmed by the reader, or entered by hand.
 */
export function SubjectsPage() {
  const { data, error, loading, reload } = useAsync(() => api.subjects(), []);
  const list = data?.subjects ?? [];
  return (
    <>
      <PageIntro eyebrow="Deine Subjekte" title="Wer dich interessiert, über alle Meldungen.">
        Ein Subjekt ist ein Unternehmen, eine Organisation, eine Behörde oder eine Person in öffentlicher Rolle. Hugarheim findet es in allen
        Meldungen über seine Namen und zeigt, wo es Hauptakteur ist und wo es nur erwähnt wird.
      </PageIntro>
      <NewSubject onCreated={reload} />
      <h2>Verfolgte Subjekte</h2>
      {loading && !data && <Loading />}
      {error && <ErrorState error={error} onRetry={reload} />}
      {data && list.length === 0 && (
        <EmptyState title="Noch keine Subjekte.">
          <p className="muted">Lege oben ein erstes Subjekt an. Bis dahin ändert sich an Ausgaben und Ranking nichts.</p>
        </EmptyState>
      )}
      {list.length > 0 && (
        <ul className="follow-list">
          {list.map((s) => (
            <li key={s.entity.id} className="follow-row">
              <div className="spread">
                <h3 className="story-title">
                  <Link to={`/subjekte/${s.entity.id}`}>{s.entity.canonical_name}</Link>
                </h3>
                <span className="row">
                  {s.entity.fixture && <Chip kind="warn">Testdaten</Chip>}
                  <Chip kind={s.follow?.status === 'active' ? 'ok' : 'neutral'}>{s.follow?.status === 'paused' ? 'pausiert' : 'aktiv'}</Chip>
                </span>
              </div>
              <div className="meta">
                <span>{KIND_LABELS[s.entity.kind]}</span>
                <span>{s.counts.main ?? 0} als Hauptakteur</span>
                <span>{s.counts.mention ?? 0} Erwähnungen</span>
                {(s.counts.unresolved ?? 0) > 0 && <span>{s.counts.unresolved} unklar</span>}
                {s.matching?.caught_up_at ? <span>abgeglichen {relativeAge(s.matching.caught_up_at)}</span> : <span>Abgleich läuft</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function emptyInput(): EntityInput {
  return { kind: 'company', canonical_name: '', aliases: [], identifiers: [], instruments: [], origin: 'manual' };
}

export function fromProposal(p: Proposal): EntityInput {
  const aliases = (p.aliases ?? []).filter((a) => a && a.toLowerCase() !== p.name.toLowerCase()).map((a) => ({ alias: a, ambiguous: false, kind: 'name' }));
  return {
    kind: p.kind,
    canonical_name: p.name,
    description: p.description ?? null,
    country: p.country ?? null,
    origin: p.registry,
    aliases,
    identifiers: (p.identifiers ?? []).map((x) => ({ scheme: x.scheme, value: x.value, origin: x.origin })),
    instruments: (p.instruments ?? []).map((x) => ({ isin: x.isin ?? null, ticker: x.ticker ?? null, exchange: x.exchange ?? null, origin: x.origin })),
  };
}

function NewSubject({ onCreated }: { onCreated: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [state, setState] = useState<'idle' | 'looking' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<LookupResult | null>(null);
  const [existing, setExisting] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<EntityInput | null>(null);

  const lookup = async (e: FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    setState('looking');
    setError('');
    setDraft(null);
    try {
      const r = await api.subjectLookup(q.trim());
      setResult(r.lookup);
      setExisting(r.existing);
      setState('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nachschlagen fehlgeschlagen');
      setState('error');
    }
  };

  const trackExisting = async (entityId: string) => {
    try {
      await api.createSubject({ entity_id: entityId });
      onCreated();
      navigate(`/subjekte/${entityId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  };

  return (
    <section className="panel" aria-labelledby="new-subject-h">
      <h2 id="new-subject-h">Subjekt anlegen</h2>
      <form onSubmit={lookup} className="search-form">
        <label className="field-label" htmlFor="subject-q">
          Name oder Kennung (LEI, CHE-UID, ISIN, Wikidata-ID)
        </label>
        <div className="row">
          <input id="subject-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} maxLength={120} placeholder="z. B. Swisscom oder CH0008742519" />
          <button type="submit" className="button primary" disabled={state === 'looking' || !q.trim()}>
            {state === 'looking' ? 'Sucht…' : 'Nachschlagen'}
          </button>
          <button
            type="button"
            className="small-button"
            onClick={() => {
              setDraft({ ...emptyInput(), canonical_name: q.trim() });
              setResult(null);
            }}
          >
            Von Hand anlegen
          </button>
        </div>
        <p className="muted small">
          Nur das Eingegebene wird an GLEIF, Wikidata und (mit Zugang) Zefix gesendet, keine Artikel, Notizen oder deine Subjekte.
        </p>
      </form>
      {error && <Notice kind="danger">{error}</Notice>}
      {result && (
        <>
          <ul className="plain small" aria-label="Register">
            {result.registries.map((r) => (
              <li key={r.registry}>
                {REGISTRY_LABELS[r.registry] ?? r.registry}:{' '}
                {r.status === 'ok'
                  ? `${r.count} Treffer`
                  : r.status === 'empty'
                    ? 'kein Treffer'
                    : r.status === 'unavailable'
                      ? `nicht verfügbar${r.detail ? ` (${detailText(r.detail)})` : ''}`
                      : `Fehler${r.detail ? ` (${detailText(r.detail)})` : ''}`}
              </li>
            ))}
          </ul>
          {result.proposals.length === 0 && (
            <p className="muted">
              Kein Vorschlag. Du kannst das Subjekt{' '}
              <button type="button" className="text-button" onClick={() => setDraft({ ...emptyInput(), canonical_name: result.query })}>
                von Hand anlegen
              </button>
              .
            </p>
          )}
          {result.proposals.map((p) => {
            const known = existing[`${p.registry}:${p.registry_id}`];
            return (
              <div key={`${p.registry}:${p.registry_id}`} className="proposal">
                <div className="spread">
                  <strong>{p.name}</strong>
                  <span className="muted small">{(p.registries ?? [p.registry]).map((r) => REGISTRY_LABELS[r] ?? r).join(' + ')}</span>
                </div>
                <div className="meta">
                  <span>{KIND_LABELS[p.kind] ?? p.kind}</span>
                  {p.description && <span>{p.description}</span>}
                  {p.status && p.status !== 'active' && <span>Status: {p.status}</span>}
                  {(p.identifiers ?? []).map((x) => (
                    <span key={x.scheme + x.value}>
                      {SCHEME_LABELS[x.scheme]} {x.value}
                    </span>
                  ))}
                  {(p.instruments ?? []).map((x, i) => (
                    <span key={i}>Wertpapier {x.isin ?? `${x.ticker}${x.exchange ? ` (${x.exchange})` : ''}`}</span>
                  ))}
                </div>
                <div className="actions">
                  {known ? (
                    <button type="button" className="small-button" onClick={() => trackExisting(known)}>
                      Bereits bekannt – verfolgen
                    </button>
                  ) : (
                    <button type="button" className="small-button" onClick={() => setDraft(fromProposal(p))}>
                      Prüfen und bestätigen
                    </button>
                  )}
                  {p.url && (
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="small">
                      Im Register ansehen ↗
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
      {draft && (
        <EntityEditor
          initial={draft}
          submitLabel="Bestätigen und verfolgen"
          onCancel={() => setDraft(null)}
          onSubmit={async (input) => {
            try {
              const created = await api.createSubject({ entity: input });
              onCreated();
              navigate(`/subjekte/${created.entity.id}`);
              return null;
            } catch (err) {
              if (err instanceof ApiError && err.code === 'entity_exists') return 'Diese Kennung gehört bereits zu einem bekannten Subjekt.';
              return err instanceof Error ? err.message : 'Speichern fehlgeschlagen';
            }
          }}
        />
      )}
    </section>
  );
}

/**
 * The confirmation and editing form: name, kind, role, aliases with their
 * ambiguity flag, identifiers of the entity and, separately, its securities.
 */
export function EntityEditor({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: EntityInput;
  submitLabel: string;
  onSubmit: (input: EntityInput) => Promise<string | null>;
  onCancel?: () => void;
}) {
  const [e, setE] = useState<EntityInput>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = <K extends keyof EntityInput>(k: K, v: EntityInput[K]) => setE((cur) => ({ ...cur, [k]: v }));
  const setAlias = (i: number, a: Partial<EntityAlias>) => set('aliases', e.aliases.map((x, j) => (j === i ? { ...x, ...a } : x)));
  const setId = (i: number, a: Partial<EntityIdentifier>) => set('identifiers', e.identifiers.map((x, j) => (j === i ? { ...x, ...a } : x)));
  const setInst = (i: number, a: Partial<Instrument>) => set('instruments', e.instruments.map((x, j) => (j === i ? { ...x, ...a } : x)));

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError('');
    const clean: EntityInput = {
      ...e,
      aliases: e.aliases.filter((a) => a.alias.trim()),
      identifiers: e.identifiers.filter((x) => x.value.trim()),
      instruments: e.instruments.filter((x) => (x.isin ?? '').trim() || (x.ticker ?? '').trim()).map((x) => ({
        ...x,
        isin: x.isin?.trim() || null,
        ticker: x.ticker?.trim() || null,
        exchange: x.exchange?.trim() || null,
      })),
    };
    const msg = await onSubmit(clean);
    setBusy(false);
    if (msg) setError(msg);
  };

  return (
    <form className="entity-editor" onSubmit={submit} aria-label="Subjekt bestätigen">
      <label className="field-label" htmlFor="ent-name">
        Name
      </label>
      <input id="ent-name" type="text" value={e.canonical_name} onChange={(x) => set('canonical_name', x.target.value)} required maxLength={200} />
      <label className="field-label" htmlFor="ent-kind">
        Art
      </label>
      <select id="ent-kind" value={e.kind} onChange={(x) => set('kind', x.target.value as EntityKind)}>
        {(Object.keys(KIND_LABELS) as EntityKind[]).map((k) => (
          <option key={k} value={k}>
            {KIND_LABELS[k]}
          </option>
        ))}
      </select>
      {e.kind === 'person' && (
        <>
          <label className="field-label" htmlFor="ent-role">
            Öffentliche Rolle (Amt, Unternehmen, Sport) – Privatpersonen werden nicht verfolgt
          </label>
          <input id="ent-role" type="text" value={e.public_role ?? ''} onChange={(x) => set('public_role', x.target.value)} required />
        </>
      )}

      <fieldset>
        <legend className="field-label">Weitere Namen</legend>
        <p className="muted small">
          Ein mehrdeutiger Name (etwa „Post“ oder „Zurich“) zählt nie allein: Meldungen, die nur ihn enthalten, erscheinen als „unklar“, bis du
          entscheidest.
        </p>
        {e.aliases.map((a, i) => (
          <div key={i} className="alias-row">
            <input type="text" aria-label={`Name ${i + 1}`} value={a.alias} onChange={(x) => setAlias(i, { alias: x.target.value })} />
            <label className="small">
              <input type="checkbox" checked={a.ambiguous} onChange={(x) => setAlias(i, { ambiguous: x.target.checked })} /> mehrdeutig
            </label>
            <button type="button" className="text-button danger" onClick={() => set('aliases', e.aliases.filter((_, j) => j !== i))}>
              Entfernen
            </button>
          </div>
        ))}
        <button type="button" className="small-button" onClick={() => set('aliases', [...e.aliases, { alias: '', ambiguous: false, kind: 'name' }])}>
          Namen hinzufügen
        </button>
      </fieldset>

      <fieldset>
        <legend className="field-label">Kennungen des Subjekts</legend>
        {e.identifiers.map((x, i) => (
          <div key={i} className="id-row">
            <select aria-label={`Art der Kennung ${i + 1}`} value={x.scheme} onChange={(v) => setId(i, { scheme: v.target.value as EntityIdentifier['scheme'] })}>
              {(Object.keys(SCHEME_LABELS) as EntityIdentifier['scheme'][]).map((s) => (
                <option key={s} value={s}>
                  {SCHEME_LABELS[s]}
                </option>
              ))}
            </select>
            <input type="text" aria-label={`Kennung ${i + 1}`} value={x.value} onChange={(v) => setId(i, { value: v.target.value })} />
            <button type="button" className="text-button danger" onClick={() => set('identifiers', e.identifiers.filter((_, j) => j !== i))}>
              Entfernen
            </button>
          </div>
        ))}
        <button type="button" className="small-button" onClick={() => set('identifiers', [...e.identifiers, { scheme: 'lei', value: '' }])}>
          Kennung hinzufügen
        </button>
      </fieldset>

      <fieldset>
        <legend className="field-label">Wertpapiere (eine ISIN bezeichnet ein Wertpapier, nicht das Unternehmen)</legend>
        {e.instruments.map((x, i) => (
          <div key={i} className="id-row">
            <input type="text" aria-label={`ISIN ${i + 1}`} placeholder="ISIN" value={x.isin ?? ''} onChange={(v) => setInst(i, { isin: v.target.value })} />
            <input type="text" aria-label={`Ticker ${i + 1}`} placeholder="Ticker" value={x.ticker ?? ''} onChange={(v) => setInst(i, { ticker: v.target.value })} />
            <input type="text" aria-label={`Börse ${i + 1}`} placeholder="Börse" value={x.exchange ?? ''} onChange={(v) => setInst(i, { exchange: v.target.value })} />
            <button type="button" className="text-button danger" onClick={() => set('instruments', e.instruments.filter((_, j) => j !== i))}>
              Entfernen
            </button>
          </div>
        ))}
        <button type="button" className="small-button" onClick={() => set('instruments', [...e.instruments, { isin: '' }])}>
          Wertpapier hinzufügen
        </button>
      </fieldset>

      {error && <Notice kind="danger">{error}</Notice>}
      <div className="button-row">
        <button type="submit" className="button primary" disabled={busy || e.canonical_name.trim().length < 2}>
          {busy ? 'Speichert…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="small-button" onClick={onCancel}>
            Abbrechen
          </button>
        )}
      </div>
    </form>
  );
}
